/**
 * OCP-985 - the `agentLanes` entitlement contract (pricing decision ORB-D97,
 * Daniel 2026-10-30; tier decisions recorded on epic OCP-984).
 *
 * An agent lane is an orboto-hosted runtime that runs one agent around the
 * clock (49 EUR per lane per month). The control plane issues how many lanes
 * a workspace PAYS for; orboto core (ORB-2710) counts the orboto-hosted lanes
 * in use and shows a license warning over the paid number. Limit, do not
 * gate: no feature is ever locked on this number.
 *
 * Shape (identical in every place the tenant reads its entitlement - the
 * self-host license JWT claim `agentLanes`, the `/v1/license/verify` and
 * `/v1/license/entitlements` responses, and the hosted workspace env
 * `LICENSE_AGENT_LANES` as JSON):
 *
 *   agentLanes: { included: number, booked: number }
 *
 *   - `included`: lanes the plan carries (tier default or operator override);
 *   - `booked`:   lanes bought on top (Polar lane add-on, OCP-987; until then
 *                 the stored value set by the audited operator override);
 *   - paid = included + booked.
 *
 * Backward compatible: a token / response / env without the field means
 * 0 included and 0 booked (`parseAgentLanes`). No token schema version is
 * bumped - the license JWT carries no version claim, and the field is an
 * additive optional claim that every old verifier ignores.
 *
 * Tier defaults:
 *   - Cloud Team 0, Cloud Business 1 (OCP-984 decision 2026-10-30);
 *   - Cloud Enterprise 1: the decision names only Team and Business;
 *     Enterprise ranks above Business in the tier order, so it gets at least
 *     Business's lane. Any other number per contract is an operator override;
 *   - partner site package: 3 per site (replaces the tier default while the
 *     workspace carries partner sites);
 *   - Self-Host: none. Lanes on the customer's own infrastructure are not
 *     counted and carry no fee, so a self-host license always states
 *     `included: 0` explicitly (never an absent field).
 */
import { z } from 'zod';

/** The list price of one hosted agent lane (ORB-D97). */
export const AGENT_LANE_PRICE_EUR_PER_MONTH = 49;

/** Included lanes per Cloud plan tier (OCP-984 decisions). */
export const AGENT_LANES_INCLUDED_BY_CLOUD_TIER = {
  team: 0,
  business: 1,
  enterprise: 1,
} as const;

/** Lanes the partner site package carries per site (ORB-D97). */
export const AGENT_LANES_PER_PARTNER_SITE = 3;

/** Self-Host counts no lanes (they run on the customer's own infrastructure). */
export const AGENT_LANES_INCLUDED_SELF_HOST = 0;

/** Upper bound for any stored lane number (sanity rail, not a product limit). */
export const AGENT_LANES_MAX = 100_000;

/** Env key a hosted workspace reads its entitlement from (JSON of `AgentLanes`). */
export const AGENT_LANES_ENV_KEY = 'LICENSE_AGENT_LANES';

/** Operator override reason bounds (OCP-599 safety rail: required + audited). */
export const AGENT_LANES_REASON_MIN = 10;
export const AGENT_LANES_REASON_MAX = 500;

export const agentLanesSchema = z.object({
  included: z.number().int().min(0),
  booked: z.number().int().min(0),
});

export type AgentLanes = z.infer<typeof agentLanesSchema>;

export const NO_AGENT_LANES: AgentLanes = Object.freeze({ included: 0, booked: 0 }) as AgentLanes;

/**
 * Read an `agentLanes` value from a token claim, a response field or the env
 * JSON. Missing, null or malformed -> 0/0 (the backward-compatible default);
 * never throws. A string is parsed as JSON (the env form).
 */
export function parseAgentLanes(raw: unknown): AgentLanes {
  let value = raw;
  if (typeof value === 'string') {
    if (value.trim() === '') return { ...NO_AGENT_LANES };
    try {
      value = JSON.parse(value);
    } catch {
      return { ...NO_AGENT_LANES };
    }
  }
  const parsed = agentLanesSchema.safeParse(value);
  return parsed.success ? { included: parsed.data.included, booked: parsed.data.booked } : { ...NO_AGENT_LANES };
}

/** paid = included + booked. */
export function paidAgentLanes(lanes: AgentLanes): number {
  return lanes.included + lanes.booked;
}

/** The env value for a hosted workspace (stable key order). */
export function agentLanesEnvValue(lanes: AgentLanes): string {
  return JSON.stringify({ included: lanes.included, booked: lanes.booked });
}

export type AgentLanesDeployment = 'cloud' | 'self_host';

/** Where the effective `included` comes from. */
export type AgentLanesIncludedSource = 'tier_default' | 'partner_package' | 'self_host' | 'override' | 'no_tier';

export interface AgentLanesDefaultInput {
  deployment: AgentLanesDeployment;
  /** Plan tier (team | business | enterprise for Cloud; any tier for a license). Null = unknown. */
  tier: string | null;
  /** Partner site package: number of sites the workspace carries (0 = none). */
  partnerSites?: number;
}

/** The tier default for `included` and where it comes from (no override applied). */
export function defaultIncludedAgentLanes(input: AgentLanesDefaultInput): { included: number; source: Exclude<AgentLanesIncludedSource, 'override'> } {
  if (input.deployment === 'self_host') return { included: AGENT_LANES_INCLUDED_SELF_HOST, source: 'self_host' };
  const sites = Math.max(0, Math.floor(input.partnerSites ?? 0));
  // Clamped to AGENT_LANES_MAX: core validates `included` as 0..100000 and would read anything above as 0/0.
  if (sites > 0) return { included: Math.min(AGENT_LANES_MAX, sites * AGENT_LANES_PER_PARTNER_SITE), source: 'partner_package' };
  const tier = input.tier as keyof typeof AGENT_LANES_INCLUDED_BY_CLOUD_TIER | null;
  if (tier && Object.prototype.hasOwnProperty.call(AGENT_LANES_INCLUDED_BY_CLOUD_TIER, tier)) {
    return { included: AGENT_LANES_INCLUDED_BY_CLOUD_TIER[tier], source: 'tier_default' };
  }
  return { included: 0, source: 'no_tier' };
}

/**
 * The effective entitlement: the operator override of `included` wins over
 * the tier default; `booked` is the stored count.
 */
export function resolveAgentLanes(
  input: AgentLanesDefaultInput & { includedOverride: number | null; booked: number },
): { lanes: AgentLanes; source: AgentLanesIncludedSource; tierDefault: number } {
  const def = defaultIncludedAgentLanes(input);
  const included = input.includedOverride ?? def.included;
  return {
    lanes: { included, booked: Math.max(0, input.booked) },
    source: input.includedOverride !== null ? 'override' : def.source,
    tierDefault: def.included,
  };
}

/** The operator view of one entitlement (admin REST, MCP, admin UI). */
export const agentLanesViewSchema = z.object({
  included: z.number().int(),
  booked: z.number().int(),
  paid: z.number().int(),
  deployment: z.enum(['cloud', 'self_host']),
  tier: z.string().nullable(),
  included_source: z.enum(['tier_default', 'partner_package', 'self_host', 'override', 'no_tier']),
  tier_default: z.number().int(),
  included_override: z.number().int().nullable(),
  /** Partner site package sites (hosted workspaces only; 0 for a license). */
  partner_sites: z.number().int(),
  /** How a change reaches the tenant. */
  delivery: z.string(),
});

export type AgentLanesView = z.infer<typeof agentLanesViewSchema>;

/**
 * The operator override body (REST `PATCH .../agent-lanes`, MCP
 * `ocp_set_agent_lanes`): at least one value plus a required reason.
 * `included_override: null` clears the override back to the tier default.
 */
export const setAgentLanesBodySchema = z.object({
  booked: z.number().int().min(0).max(AGENT_LANES_MAX).optional(),
  included_override: z.number().int().min(0).max(AGENT_LANES_MAX).nullable().optional(),
  partner_sites: z.number().int().min(0).max(AGENT_LANES_MAX).optional(),
  reason: z.string().trim().min(AGENT_LANES_REASON_MIN).max(AGENT_LANES_REASON_MAX),
});

export type SetAgentLanesBody = z.infer<typeof setAgentLanesBodySchema>;
